import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  FormSectionSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type RequestPolicePresenceSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["requestPolicePresence"]["formDefinition"]["sections"][0]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function RequestPolicePresenceFormSkeleton() {
  return (
    <MultiPageFormSkeleton headerText={tk("requestPolicePresence.title")} />
  );
}

///

export type RequestPolicePresenceFormProps<T extends WorkflowVersion> =
  Prettify<
    {
      workflowVersion: T;
    } & Pick<
      MultiPageFormProps<RequestPolicePresenceSchema<T>>,
      | "initialValues"
      | "incidentWorkItem"
      | "onSubmit"
      | "onCancel"
      | "onAutoSave"
    >
  >;

///

export function RequestPolicePresenceForm<T extends WorkflowVersion>(
  props: RequestPolicePresenceFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const section = workflow?.requestPolicePresence.formDefinition.sections.find(
    (section) =>
      section.id === FormSectionSchema.enum.requestPolicePresenceMain,
  );

  const formSpecification = section?.buildFormSpecification({
    incidentWorkItem: props.incidentWorkItem,
  });
  const formSchema = formSpecification?.formSchema;
  const strictFieldSchemas = formSpecification?.strictFieldSchemas;
  const defaultValues = formSpecification?.defaultValues;

  const initialValuesOrDefaults = useMemo(() => {
    if (!section || !strictFieldSchemas || !formSchema) {
      return {};
    }
    return createInitialValues(strictFieldSchemas, defaultValues);
  }, [defaultValues, strictFieldSchemas, section, formSchema]);

  if (!section || !formSchema || !strictFieldSchemas) {
    return <Error />;
  }

  return (
    <MultiPageForm<typeof formSchema>
      headerText={tk("requestPolicePresence.title")}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onAutoSave={props.onAutoSave}
    />
  );
}
